import { useState, useEffect } from 'react';
import { BookMarked, Sparkles, HelpCircle, Plug, Activity, Tags, Users, Languages, Loader } from 'lucide-react';
import { listModules, getIntegrations, getTracking, getMetadata, getTeam, getSupportedLanguages } from '../services/elearning';

export default function Fusette() {
  const [activeTab, setActiveTab] = useState('modules');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const tabs = [
    { id: 'modules', label: 'Modules E-learning', icon: Sparkles },
    { id: 'lms', label: 'Intégration LMS', icon: Plug },
    { id: 'tracking', label: 'Tracking', icon: Activity },
    { id: 'collaboration', label: 'Co-édition', icon: Users },
    { id: 'translation', label: 'Traduction', icon: Languages }
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      let result;
      switch (activeTab) {
        case 'modules': result = await listModules(); break;
        case 'lms': result = await getIntegrations(); break;
        case 'tracking': result = await getTracking(1); break;
        case 'collaboration': result = await getTeam(1); break;
        case 'translation': result = await getSupportedLanguages(); break;
      }
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [activeTab]);

  const renderContent = () => {
    if (loading) return <div className="flex items-center justify-center py-12"><Loader className="w-8 h-8 animate-spin text-pink-600" /></div>;

    switch (activeTab) {
      case 'modules':
        return data?.modules?.map((mod, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-5 mb-3 hover:shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-gray-900">{mod.title}</div>
              <span className={`px-3 py-1 text-xs rounded-full ${mod.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{mod.status}</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
              <div>{mod.enrollments} inscrits</div>
              <div>{mod.completion}% complétude</div>
              <div>★ {mod.rating}/5</div>
            </div>
          </div>
        ));

      case 'lms':
        return data?.integrations?.map((int, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-5 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">{int.name}</div>
              <span className={`px-3 py-1 text-xs rounded-full ${int.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{int.status}</span>
            </div>
            <div className="text-sm text-gray-600">Type: {int.type} • {int.courses} cours • Dernière sync: {int.lastSync}</div>
          </div>
        ));

      case 'tracking':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{data?.tracking?.stats?.averageProgress}%</div>
                <div className="text-xs text-gray-600">Progression moy.</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{data?.tracking?.stats?.completions}</div>
                <div className="text-xs text-gray-600">Complétés</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{data?.tracking?.stats?.totalEnrollments}</div>
                <div className="text-xs text-gray-600">Inscrits</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">{data?.tracking?.stats?.averageScore}%</div>
                <div className="text-xs text-gray-600">Score moyen</div>
              </div>
            </div>
            {data?.tracking?.users?.map((user, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium">{user.userName}</div>
                  <div className="text-xl font-bold text-indigo-600">{user.progress}%</div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${user.progress}%` }} />
                </div>
                <div className="text-sm text-gray-600">Quiz: {user.quizzesScore}% • Temps: {user.timeSpent}min</div>
              </div>
            ))}
          </div>
        );

      case 'collaboration':
        return data?.team?.map((member, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-4 mb-3 flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-900">{member.name}</div>
              <div className="text-sm text-gray-600">{member.role} • {member.contributions} contributions</div>
            </div>
            <div className="text-xs text-gray-500">{member.lastEdit}</div>
          </div>
        ));

      case 'translation':
        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data?.languages?.map((lang, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-5 hover:border-pink-300 cursor-pointer">
                <div className="text-2xl mb-2">{lang.native}</div>
                <div className="text-sm text-gray-600">{lang.name}</div>
              </div>
            ))}
          </div>
        );

      default:
        return <div className="text-center text-gray-500 py-12">Sélectionnez un onglet</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white shadow-lg">
              <BookMarked className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Fusette</h1>
              <p className="text-gray-600">Modules LMS - Génération de contenu pédagogique</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${activeTab === tab.id ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`}>
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">{renderContent()}</div>
      </div>
    </div>
  );
}
